'use client';
import { useState, useRef, useEffect } from 'react';
import { components } from 'react-select';
import { mutate } from 'swr';
import {
  Wrapper,
  Select,
  OptionCheck,
  OptionWrapper,
  ImageControl,
} from './styles';
import { Image } from '@/shared/ui/image';
import { useCurrency } from '../model/hooks';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { BodyTextBoldSecondary } from '@/shared/ui/typography';
import { formatCurrency } from '@/shared/libs/format-currency';
import { useI18n } from '@locales/client';
import { useOnClickOutside } from 'usehooks-ts';
import { Condition } from '@/shared/ui/condition';

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Image
        src="/images/icons/indicator-lang.svg"
        width={16}
        height={16}
        style={
          props.selectProps.menuIsOpen
            ? {
                transition: 'all .2s ease',
                transform: 'rotate(0deg)',
                marginLeft: '8px',
              }
            : {
                transition: 'all .2s ease',
                transform: 'rotate(180deg)',
                marginLeft: '8px',
              }
        }
        alt="indicator"
      />
    </components.DropdownIndicator>
  );
};

export const SelectCurrency = ({ profileMode, authMode }) => {
  const {
    data: currencies,
    trigger: changeCurrencyTrigger,
    mutate: currencyMutate,
    isLoading,
  } = useCurrency();
  const [isFocused, setIsFocused] = useState(false)<Boolean>;
  const [menuIsOpen, setMenuIsOpen] = useState(false)<Boolean>;
  const outsideRef = useRef(null);
  const t = useI18n();
  const { setUser, user } = useUser();
  useOnClickOutside(outsideRef, () => {
    setMenuIsOpen(false);
    setIsFocused(false);
  });

  useEffect(() => {
    if (authMode?.authModeActive && currencies?.length > 0) {
      authMode?.setFormWelcomBanner((res) => {
        return {
          ...res,
          currency: authMode?.formWelcomBanner?.currency
            ? authMode.formWelcomBanner.currency
            : currencies?.[0]?.currency,
        };
      });
    }
  }, [currencies, authMode?.authModeActive]);

  if (isLoading) {
    return null;
  }

  const ValueContainer = ({ children, ...props }) => (
    <components.ValueContainer {...props}>
      <BodyTextBoldSecondary>
        <Condition match={profileMode || authMode?.authModeActive}>
          {props?.getValue()[0]?.currency}
        </Condition>
        <Condition match={!profileMode && !authMode?.authModeActive}>
          {t('wallet.change_currency')}
        </Condition>
        {children[1]}
      </BodyTextBoldSecondary>
    </components.ValueContainer>
  );

  const Option = ({ children, ...props }) => {
    return (
      <components.Option {...props}>
        <OptionWrapper
          $isSelected={props.isSelected}
          onClick={() => setMenuIsOpen(false)}
        >
          <>
            <ImageControl
              alt="country"
              width={20}
              height={20}
              src={props.data.imageUrl}
            />
          </>
          <>{children}</>
          {props.isSelected && (
            <OptionCheck>
              <Image
                src={'/images/icons/check.svg'}
                width={16}
                height={16}
                alt="Checkmark"
              />
            </OptionCheck>
          )}
        </OptionWrapper>
      </components.Option>
    );
  };

  const changeCurrency = async (option) => {
    const { userDepositBonus } = user || {};
    const bonusId = userDepositBonus?.id ? `?bonus=${userDepositBonus.id}` : '';

    if (authMode?.authModeActive) {
      authMode?.setFormWelcomBanner((res) => {
        return {
          ...res,
          currency: option?.currency,
        };
      });
      return;
    }

    const urlMutate = [
      `/payment/fiat/payment/methods${bonusId}`,
      '/payment/fiat/payout/methods',
      '/profile/getBonuses',
      '/payment/history',
      '/shop',
    ];

    await changeCurrencyTrigger(option.currency);

    setUser((prev) => ({
      ...prev,
      currency: option.currency,
      currencySymbol: option.currencySymbol,
    }));

    await currencyMutate();
    await Promise.all(urlMutate.map((urlEl) => mutate(urlEl)));
  };

  const currentOption = currencies?.find((currency) => {
    if (authMode?.authModeActive) {
      return currency?.currency === authMode?.formWelcomBanner?.currency;
    }
    return currency?.active;
  });

  const styles = {
    menu: (provided) => ({
      ...provided,
      position: 'absolute',
      [profileMode ? 'left' : 'right']: 0,
    }),
  };
  return (
    <Wrapper>
      <Select
        styles={styles}
        $minWidth={authMode?.authModeActive}
        $isFocused={isFocused}
        $profileMode={profileMode || authMode?.authModeActive}
        options={currencies}
        value={currentOption}
        unstyled
        delimiter={false}
        isSearchable={false}
        components={{ DropdownIndicator, Option, ValueContainer }}
        getOptionLabel={(option) =>
          !authMode?.authModeActive
            ? `${formatCurrency(option.balance, {
                currency: option.currency,
                currencySymbol: option.currencySymbol,
              })}`
            : option.currencySymbol
        }
        getOptionValue={(option) => option.currency}
        isFocused={isFocused}
        classNamePrefix={'currency-select'}
        onChange={(option) => changeCurrency(option)}
      />
    </Wrapper>
  );
};
